import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';

const ViewTimeTable = () => {
  const { currentUser } = useAuth(); // Use AuthContext instead of Supabase auth
  // State for timetable data
  const [timetables, setTimetables] = useState([]);
  const [filteredTimetables, setFilteredTimetables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUniversityId, setCurrentUniversityId] = useState(null);
  const [authError, setAuthError] = useState(null); // Add auth error state
  
  // State for filters
  const [filters, setFilters] = useState({
    search: '',
    degree: '',
    year: '',
    semester: '',
    academicYear: '',
    dayOfWeek: '',
    classType: '',
    lecturer: '',
    module: '',
    group: ''
  });
  
  // State for view options
  const [viewMode, setViewMode] = useState('list'); // 'list', 'grid', 'calendar'
  const [groupBy, setGroupBy] = useState('day'); // 'day', 'module', 'lecturer'
  const [sortBy, setSortBy] = useState('day_time'); // 'day_time', 'module', 'lecturer'
  
  // State for dropdown options
  const [degrees, setDegrees] = useState([]);
  const [lecturers, setLecturers] = useState([]);
  const [modules, setModules] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  
  // State for detailed view modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTimetable, setSelectedTimetable] = useState(null);

  useEffect(() => {
    if (currentUser) {
      fetchUniversityData();
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUniversityId) {
      fetchTimetables();
    }
  }, [currentUniversityId]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [filters, timetables, sortBy]);

  useEffect(() => {
    // Update groups when filter dependencies change
    if (currentUniversityId) {
      fetchFilteredGroups();
    }
  }, [filters.degree, filters.year, filters.semester, filters.academicYear, currentUniversityId]);

  const fetchUniversityData = async () => {
    try {
      setLoading(true);
      setAuthError(null); // Reset auth error
      
      console.log('🔍 Starting fetchUniversityData in ViewTimeTable...');
      
      // Check if user is authenticated through AuthContext
      if (!currentUser || !currentUser.email) {
        console.error('❌ No authenticated user found in AuthContext');
        setAuthError('Authentication failed. Please log in again.');
        setLoading(false);
        return;
      }

      console.log('✅ Current authenticated user from AuthContext:', currentUser.email);
      console.log('👤 Current user role:', currentUser.role);
      console.log('🆔 Current user ID:', currentUser.id);

      // Verify this is an admin user
      if (currentUser.role !== 'admin') {
        console.error('❌ User is not an admin:', currentUser.role);
        setAuthError('Access denied. This page is for administrators only.');
        setLoading(false);
        return;
      }

      // Get admin's university_id with more detailed logging
      console.log('🔍 Searching for admin with email:', currentUser.email);
      const { data: adminData, error: adminError } = await supabase
        .from('admins')
        .select('university_id, id, email')
        .eq('email', currentUser.email)
        .single();

      console.log('📋 Admin query result:', { adminData, adminError });

      if (adminError || !adminData) {
        console.error('❌ Error fetching admin data:', adminError);
        console.error('❌ Admin not found for email:', currentUser.email);
        
        // Let's also check what admins exist in the database
        console.log('🔍 Checking all admins in database...');
        const { data: allAdmins, error: allAdminsError } = await supabase
          .from('admins')
          .select('email, university_id, active');
        
        console.log('📋 All admins in database:', allAdmins);
        console.log('📋 All admins query error:', allAdminsError);
        
        setAuthError('Admin access not found. Please contact your system administrator.');
        setLoading(false);
        return;
      }
      
      const universityId = adminData.university_id;
      setCurrentUniversityId(universityId);
      
      console.log('✅ Admin found!');
      console.log('🎓 Admin university ID:', universityId);
      console.log('👤 Admin ID:', adminData.id);
      console.log('📧 Admin email:', adminData.email);

      // Fetch filter options
      await Promise.all([
        fetchDegrees(universityId),
        fetchLecturers(universityId),
        fetchModules(universityId),
        fetchAcademicYears(universityId),
        fetchAllGroups(universityId)
      ]);

    } catch (error) {
      console.error('Error in fetchUniversityData:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDegrees = async (universityId) => {
    try {
      const { data, error } = await supabase
        .from('degree_details')
        .select('id, degree_programme, degree_level')
        .eq('university_id', universityId)
        .eq('active', true)
        .order('degree_programme');

      if (!error) {
        setDegrees(data || []);
      }
    } catch (error) {
      console.error('Error fetching degrees:', error);
    }
  };

  const fetchLecturers = async (universityId) => {
    try {
      const { data, error } = await supabase
        .from('staff')
        .select('id, name, staff_type')
        .eq('university_id', universityId)
        .eq('active', true)
        .in('staff_type', ['lecturer', 'professor', 'assistant_lecturer'])
        .order('name');

      if (!error) {
        setLecturers(data || []);
      }
    } catch (error) {
      console.error('Error fetching lecturers:', error);
    }
  };

  const fetchModules = async (universityId) => {
    try {
      const { data, error } = await supabase
        .from('modules')
        .select('id, module_name, module_code')
        .eq('university_id', universityId)
        .eq('active', true)
        .order('module_code');

      if (!error) {
        setModules(data || []);
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  const fetchAcademicYears = async (universityId) => {
    try {
      const { data, error } = await supabase
        .from('timetables')
        .select('academic_year')
        .eq('university_id', universityId)
        .eq('active', true);

      if (!error) {
        const dbYears = [...new Set(data.map(t => t.academic_year).filter(Boolean))];
        
        // Add standard academic years to ensure they're always available
        const standardYears = ['2023-2024', '2024-2025', '2025-2026', '2026-2027', '2027-2028'];
        
        // Combine database years with standard years and remove duplicates
        const allYears = [...new Set([...dbYears, ...standardYears])];
        
        setAcademicYears(allYears.sort());
      }
    } catch (error) {
      console.error('Error fetching academic years:', error);
    }
  };

  const fetchAllGroups = async (universityId) => {
    try {
      const { data, error } = await supabase
        .from('groups')
        .select(`
          id,
          group_name,
          degree_details_id,
          year,
          semester,
          academic_year,
          degree_details:degree_details_id (
            id,
            degree_programme,
            degree_level
          )
        `)
        .eq('university_id', universityId)
        .eq('active', true)
        .order('group_name');

      if (!error) {
        setGroups(data || []);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchFilteredGroups = async () => {
    try {
      if (!currentUniversityId) return;

      let query = supabase
        .from('groups')
        .select(`
          id,
          group_name,
          degree_details_id,
          year,
          semester,
          academic_year
        `)
        .eq('university_id', currentUniversityId)
        .eq('active', true);

      // Apply filters based on selected criteria
      if (filters.degree) {
        query = query.eq('degree_details_id', filters.degree);
      }

      if (filters.year) {
        const yearNumber = parseInt(filters.year.replace('Year ', ''));
        query = query.eq('year', yearNumber);
      }

      if (filters.semester) {
        const semesterNumber = parseInt(filters.semester.replace('Semester ', ''));
        query = query.eq('semester', semesterNumber);
      }

      if (filters.academicYear) {
        query = query.eq('academic_year', filters.academicYear);
      }

      const { data, error } = await query.order('group_name');

      if (!error) {
        setFilteredGroups(data || []);
      }
    } catch (error) {
      console.error('Error fetching filtered groups:', error);
    }
  };

  const fetchTimetables = async () => {
    try {
      setLoading(true);
      
      if (!currentUniversityId) {
        console.warn('⚠️ No university ID available, skipping timetables fetch in ViewTimeTable');
        return;
      }
      
      console.log('🔍 Fetching timetables for university ID in ViewTimeTable:', currentUniversityId);
      
      const { data: timetablesData, error } = await supabase
        .from('timetables')
        .select(`
          *,
          modules:module_id (
            id,
            module_name,
            module_code
          ),
          lecturer:lecturer_id (
            id,
            name,
            staff_type
          ),
          assistant_lecturer:assistant_lecturer_id (
            id,
            name,
            staff_type
          )
        `)
        .eq('university_id', currentUniversityId)
        .eq('active', true)
        .order('day_of_week')
        .order('start_time');

      console.log('📋 ViewTimeTable timetables query result:', { timetablesData, error });

      if (error) {
        console.error('❌ Error fetching timetables in ViewTimeTable:', error);
        return;
      }

      console.log('✅ Fetched timetables for university in ViewTimeTable:', timetablesData?.length || 0, 'records');
      
      // Log each timetable's university ID for verification
      (timetablesData || []).forEach((timetable, index) => {
        console.log(`📝 ViewTimeTable Timetable ${index + 1}: Module "${timetable.modules?.module_name}" (University ID: ${timetable.university_id})`);
      });

      // Process timetables and fetch additional data
      const processedTimetables = [];
      
      for (const timetable of (timetablesData || [])) {
        // Fetch groups for this timetable
        const { data: groupsData } = await supabase
          .from('groups')
          .select(`
            id,
            group_name,
            degree_details:degree_details_id (
              id,
              degree_programme,
              degree_level
            )
          `)
          .in('id', timetable.group_ids || [])
          .eq('active', true);

        // Fetch degree details
        const { data: degreeData } = await supabase
          .from('degree_details')
          .select('id, degree_programme, degree_level')
          .in('id', timetable.degree_details_ids || [])
          .eq('active', true);

        let groupNames = '';
        let degreeNames = '';
        
        if (groupsData) {
          groupNames = groupsData.map(g => g.group_name).join(', ');
        }

        if (degreeData) {
          degreeNames = degreeData.map(d => `${d.degree_programme} (${d.degree_level})`).join(', ');
        }

        processedTimetables.push({
          ...timetable,
          groupNames,
          degreeNames,
          groups: groupsData || [],
          degrees: degreeData || [],
          timeDisplay: formatTimeDisplay(timetable.start_time, timetable.end_time),
          dayOrder: getDayOrder(timetable.day_of_week)
        });
      }
      
      // Let's also check if there are timetables from other universities (for debugging)
      console.log('🔍 Checking timetables from other universities in ViewTimeTable (for debugging)...');
      const { data: allTimetablesData, error: allTimetablesError } = await supabase
        .from('timetables')
        .select('id, university_id, modules:module_id(module_name), universities:university_id(name)')
        .eq('active', true);
      
      console.log('📋 All timetables in database (ViewTimeTable):', allTimetablesData?.length || 0);
      if (allTimetablesData) {
        const otherUniversityTimetables = allTimetablesData.filter(t => t.university_id !== currentUniversityId);
        console.log('🚨 Timetables from other universities (ViewTimeTable):', otherUniversityTimetables.length);
        if (otherUniversityTimetables.length > 0) {
          console.log('🚨 Sample other university timetables (ViewTimeTable):', otherUniversityTimetables.slice(0, 3));
        }
      }
      
      setTimetables(processedTimetables);
    } catch (error) {
      console.error('Error in fetchTimetables:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeDisplay = (startTime, endTime) => {
    // Extract time directly from UTC timestamp without timezone conversion
    // Since we store as 1970-01-01T14:30:00.000Z, we need to get UTC hours/minutes
    const start = new Date(startTime);
    const end = new Date(endTime);
    
    // Get UTC hours and minutes to match what was originally selected
    const startHours = start.getUTCHours();
    const startMinutes = start.getUTCMinutes();
    const endHours = end.getUTCHours();
    const endMinutes = end.getUTCMinutes();
    
    // Format as 12-hour time
    const formatTime = (hours, minutes) => {
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      return `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
    };
    
    return `${formatTime(startHours, startMinutes)} - ${formatTime(endHours, endMinutes)}`;
  };

  const getDayOrder = (day) => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return days.indexOf(day);
  };

  const applyFiltersAndSort = () => {
    let filtered = [...timetables];

    // Apply search filter
    if (filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(tt => 
        tt.modules?.module_name?.toLowerCase().includes(searchTerm) ||
        tt.modules?.module_code?.toLowerCase().includes(searchTerm) ||
        tt.lecturer?.name?.toLowerCase().includes(searchTerm) ||
        tt.assistant_lecturer?.name?.toLowerCase().includes(searchTerm) ||
        tt.venue?.toLowerCase().includes(searchTerm) ||
        tt.groupNames?.toLowerCase().includes(searchTerm) ||
        tt.degreeNames?.toLowerCase().includes(searchTerm) ||
        tt.class_type?.toLowerCase().includes(searchTerm)
      );
    }

    // Apply other filters
    if (filters.degree) {
      filtered = filtered.filter(tt => 
        tt.degree_details_ids && tt.degree_details_ids.includes(filters.degree)
      );
    }

    if (filters.year) {
      const yearNumber = parseInt(filters.year.replace('Year ', ''));
      filtered = filtered.filter(tt => tt.year === yearNumber);
    }

    if (filters.semester) {
      const semesterNumber = parseInt(filters.semester.replace('Semester ', ''));
      filtered = filtered.filter(tt => tt.semester === semesterNumber);
    }

    if (filters.academicYear) {
      filtered = filtered.filter(tt => tt.academic_year === filters.academicYear);
    }

    if (filters.dayOfWeek) {
      filtered = filtered.filter(tt => tt.day_of_week === filters.dayOfWeek);
    }

    if (filters.classType) {
      filtered = filtered.filter(tt => tt.class_type === filters.classType);
    }

    if (filters.lecturer) {
      filtered = filtered.filter(tt => 
        tt.lecturer_id === filters.lecturer || 
        tt.assistant_lecturer_id === filters.lecturer
      );
    }

    if (filters.module) {
      filtered = filtered.filter(tt => tt.module_id === filters.module);
    }

    if (filters.group) {
      filtered = filtered.filter(tt => 
        tt.group_ids && tt.group_ids.includes(filters.group)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'day_time':
          if (a.dayOrder !== b.dayOrder) {
            return a.dayOrder - b.dayOrder;
          }
          return new Date(a.start_time) - new Date(b.start_time);
        case 'module':
          return a.modules?.module_code?.localeCompare(b.modules?.module_code || '') || 0;
        case 'lecturer':
          return a.lecturer?.name?.localeCompare(b.lecturer?.name || '') || 0;
        default:
          return 0;
      }
    });

    setFilteredTimetables(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [field]: value
      };

      // Reset group filter when dependent filters change
      if (['degree', 'year', 'semester', 'academicYear'].includes(field)) {
        newFilters.group = '';
      }

      return newFilters;
    });
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      degree: '',
      year: '',
      semester: '',
      academicYear: '',
      dayOfWeek: '',
      classType: '',
      lecturer: '',
      module: '',
      group: ''
    });
  };

  const handleViewDetails = (timetable) => {
    setSelectedTimetable(timetable);
    setShowDetailModal(true);
  };

  // CSV Download Function
  const downloadCSV = () => {
    if (filteredTimetables.length === 0) {
      alert('No timetable data to download');
      return;
    }

    // Prepare CSV headers
    const headers = [
      'Module Code',
      'Module Name', 
      'Day of Week',
      'Start Time',
      'End Time',
      'Venue',
      'Class Type',
      'Lecturer',
      'Assistant Lecturer',
      'Groups',
      'Degree',
      'Year',
      'Semester',
      'Academic Year'
    ];

    // Prepare CSV rows
    const rows = filteredTimetables.map(timetable => [
      timetable.modules?.module_code || '',
      timetable.modules?.module_name || '',
      timetable.day_of_week || '',
      timetable.start_time || '',
      timetable.end_time || '',
      timetable.venue || '',
      timetable.class_type || '',
      timetable.lecturer?.name || '',
      timetable.assistant_lecturer?.name || '',
      timetable.groupNames || '',
      timetable.degree_program?.title || '',
      timetable.year || '',
      timetable.semester || '',
      timetable.academic_year || ''
    ]);

    // Create CSV content
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Generate filename with current filters
      const activeFilters = Object.entries(filters)
        .filter(([key, value]) => value !== '' && value !== 'all')
        .map(([key, value]) => `${key}-${value}`)
        .join('_');
      
      const filename = `timetable_${activeFilters || 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const renderTimetableCard = (timetable) => (
    <div
      key={timetable.id}
      style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #E5E7EB',
        transition: 'all 0.3s ease',
        cursor: 'pointer'
      }}
      onClick={() => handleViewDetails(timetable)}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
      }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '12px'
      }}>
        <div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#1F2937',
            margin: '0 0 4px 0',
            fontFamily: 'Outfit'
          }}>
            {timetable.modules?.module_code}
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#6B7280',
            margin: 0,
            fontFamily: 'Outfit'
          }}>
            {timetable.modules?.module_name}
          </p>
        </div>
        <span style={{
          padding: '4px 12px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '500',
          backgroundColor: '#FEF3F2',
          color: '#F04915',
          fontFamily: 'Outfit'
        }}>
          {timetable.class_type}
        </span>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        fontSize: '14px',
        fontFamily: 'Outfit'
      }}>
        <div>
          <strong style={{ color: '#374151' }}>Day:</strong>
          <span style={{ marginLeft: '8px', color: '#6B7280' }}>
            {timetable.day_of_week}
          </span>
        </div>
        <div>
          <strong style={{ color: '#374151' }}>Time:</strong>
          <span style={{ marginLeft: '8px', color: '#6B7280' }}>
            {timetable.timeDisplay}
          </span>
        </div>
        <div>
          <strong style={{ color: '#374151' }}>Venue:</strong>
          <span style={{ marginLeft: '8px', color: '#6B7280' }}>
            {timetable.venue}
          </span>
        </div>
        <div>
          <strong style={{ color: '#374151' }}>Lecturer:</strong>
          <span style={{ marginLeft: '8px', color: '#6B7280' }}>
            {timetable.lecturer?.name}
          </span>
        </div>
      </div>

      {timetable.groupNames && (
        <div style={{
          marginTop: '12px',
          fontSize: '12px',
          color: '#6B7280',
          fontFamily: 'Outfit'
        }}>
          <strong>Groups:</strong> {timetable.groupNames}
        </div>
      )}
    </div>
  );

  const renderListView = () => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: '20px'
    }}>
      {filteredTimetables.map(renderTimetableCard)}
    </div>
  );

  const renderTableView = () => (
    <div style={{
      overflowX: 'auto',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
    }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        background: 'white'
      }}>
        <thead>
          <tr style={{
            background: 'linear-gradient(135deg, #F04915 0%, #FF6B35 100%)'
          }}>
            <th style={{ padding: '16px 12px', color: 'white', textAlign: 'left', fontFamily: 'Outfit', fontWeight: '600' }}>Module</th>
            <th style={{ padding: '16px 12px', color: 'white', textAlign: 'left', fontFamily: 'Outfit', fontWeight: '600' }}>Day</th>
            <th style={{ padding: '16px 12px', color: 'white', textAlign: 'left', fontFamily: 'Outfit', fontWeight: '600' }}>Time</th>
            <th style={{ padding: '16px 12px', color: 'white', textAlign: 'left', fontFamily: 'Outfit', fontWeight: '600' }}>Venue</th>
            <th style={{ padding: '16px 12px', color: 'white', textAlign: 'left', fontFamily: 'Outfit', fontWeight: '600' }}>Type</th>
            <th style={{ padding: '16px 12px', color: 'white', textAlign: 'left', fontFamily: 'Outfit', fontWeight: '600' }}>Lecturer</th>
            <th style={{ padding: '16px 12px', color: 'white', textAlign: 'left', fontFamily: 'Outfit', fontWeight: '600' }}>Groups</th>
          </tr>
        </thead>
        <tbody>
          {filteredTimetables.map((timetable) => (
            <tr
              key={timetable.id}
              style={{
                borderBottom: '1px solid #E5E7EB',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onClick={() => handleViewDetails(timetable)}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#FEF3F2';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <td style={{ padding: '16px 12px', fontFamily: 'Outfit' }}>
                <div style={{ fontWeight: '600', color: '#1F2937' }}>
                  {timetable.modules?.module_code}
                </div>
                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                  {timetable.modules?.module_name}
                </div>
              </td>
              <td style={{ padding: '16px 12px', fontFamily: 'Outfit' }}>{timetable.day_of_week}</td>
              <td style={{ padding: '16px 12px', fontFamily: 'Outfit', fontSize: '13px' }}>{timetable.timeDisplay}</td>
              <td style={{ padding: '16px 12px', fontFamily: 'Outfit' }}>{timetable.venue}</td>
              <td style={{ padding: '16px 12px' }}>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '500',
                  backgroundColor: '#FEF3F2',
                  color: '#F04915',
                  fontFamily: 'Outfit'
                }}>
                  {timetable.class_type}
                </span>
              </td>
              <td style={{ padding: '16px 12px', fontFamily: 'Outfit' }}>
                <div style={{ fontWeight: '500' }}>
                  {timetable.lecturer?.name}
                </div>
                {timetable.assistant_lecturer && (
                  <div style={{ fontSize: '12px', color: '#6B7280' }}>
                    Assistant: {timetable.assistant_lecturer.name}
                  </div>
                )}
              </td>
              <td style={{ padding: '16px 12px', fontFamily: 'Outfit', fontSize: '13px', maxWidth: '150px' }}>
                {timetable.groupNames || 'No groups'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      {/* Custom styles */}
      <style>
        {`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }
          
          .modal-content {
            background: white;
            border-radius: 15px;
            padding: 30px;
            max-width: 90vw;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          }
        `}
      </style>

      <div style={{width: '100%', height: '100%', position: 'relative', background: 'white'}}>
        <Sidebar />
        
        <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh'}}>
          <Header />
          
          {/* Page Title */}
          <h1 style={{fontSize: 24, fontFamily: 'Outfit', fontWeight: '700', marginTop: '20px', paddingLeft: '40px'}}>
            View Time Tables
          </h1>

          {/* Main Container */}
          <div style={{margin: '20px 40px'}}>
            {/* Search and Filter Section */}
            <div style={{
              background: '#F04915',
              padding: '20px',
              borderTopLeftRadius: 15,
              borderTopRightRadius: 15
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <h2 style={{
                  color: 'white',
                  fontSize: 20,
                  fontFamily: 'Outfit',
                  fontWeight: '600',
                  margin: 0
                }}>
                  Search & Filter Time Tables
                </h2>
                
                {/* View Mode Toggle */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => setViewMode('grid')}
                    style={{
                      padding: '8px 16px',
                      background: viewMode === 'grid' ? 'white' : 'rgba(255,255,255,0.2)',
                      color: viewMode === 'grid' ? '#F04915' : 'white',
                      border: '2px solid white',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'Outfit',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    📋 Grid View
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    style={{
                      padding: '8px 16px',
                      background: viewMode === 'table' ? 'white' : 'rgba(255,255,255,0.2)',
                      color: viewMode === 'table' ? '#F04915' : 'white',
                      border: '2px solid white',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'Outfit',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    📊 Table View
                  </button>
                  
                  {/* CSV Download Button */}
                  {filteredTimetables.length > 0 && (
                    <button
                      onClick={downloadCSV}
                      style={{
                        padding: '8px 16px',
                        background: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        border: '2px solid white',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'Outfit',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      📥 Download CSV
                    </button>
                  )}
                </div>
              </div>

              {/* Search Bar */}
              <div style={{marginBottom: '20px'}}>
                <input
                  type="text"
                  placeholder="Search by module name, code, lecturer, venue, or group..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Outfit',
                    outline: 'none'
                  }}
                />
              </div>

              {/* Filters */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '15px',
                marginBottom: '15px'
              }}>
                {/* ...existing filter dropdowns... */}
                <select
                  value={filters.module}
                  onChange={(e) => handleFilterChange('module', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Modules</option>
                  {modules.map(module => (
                    <option key={module.id} value={module.id}>
                      {module.module_code} - {module.module_name}
                    </option>
                  ))}
                </select>

                <select
                  value={filters.degree}
                  onChange={(e) => handleFilterChange('degree', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Degrees</option>
                  {degrees.map(degree => (
                    <option key={degree.id} value={degree.id}>
                      {degree.degree_programme} ({degree.degree_level})
                    </option>
                  ))}
                </select>

                <select
                  value={filters.year}
                  onChange={(e) => handleFilterChange('year', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Years</option>
                  <option value="Year 1">Year 1</option>
                  <option value="Year 2">Year 2</option>
                  <option value="Year 3">Year 3</option>
                  <option value="Year 4">Year 4</option>
                </select>

                <select
                  value={filters.semester}
                  onChange={(e) => handleFilterChange('semester', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Semesters</option>
                  <option value="Semester 1">Semester 1</option>
                  <option value="Semester 2">Semester 2</option>
                </select>

                <select
                  value={filters.academicYear}
                  onChange={(e) => handleFilterChange('academicYear', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Academic Years</option>
                  {academicYears.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>

                <select
                  value={filters.dayOfWeek}
                  onChange={(e) => handleFilterChange('dayOfWeek', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Days</option>
                  <option value="Monday">Monday</option>
                  <option value="Tuesday">Tuesday</option>
                  <option value="Wednesday">Wednesday</option>
                  <option value="Thursday">Thursday</option>
                  <option value="Friday">Friday</option>
                  <option value="Saturday">Saturday</option>
                  <option value="Sunday">Sunday</option>
                </select>

                <select
                  value={filters.classType}
                  onChange={(e) => handleFilterChange('classType', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Class Types</option>
                  <option value="Lecture">Lecture</option>
                  <option value="Tutorial">Tutorial</option>
                  <option value="Practical">Practical</option>
                  <option value="Laboratory">Laboratory</option>
                  <option value="Workshop">Workshop</option>
                </select>

                <select
                  value={filters.lecturer}
                  onChange={(e) => handleFilterChange('lecturer', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Lecturers</option>
                  {lecturers.map(lecturer => (
                    <option key={lecturer.id} value={lecturer.id}>
                      {lecturer.name}
                    </option>
                  ))}
                </select>

                <select
                  value={filters.group}
                  onChange={(e) => handleFilterChange('group', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                  disabled={!filters.degree && !filters.year && !filters.semester && !filters.academicYear}
                >
                  <option value="">
                    {(!filters.degree && !filters.year && !filters.semester && !filters.academicYear) 
                      ? 'Select filters to show groups' 
                      : 'All Groups'}
                  </option>
                  {filteredGroups.map(group => (
                    <option key={group.id} value={group.id}>
                      {group.group_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Additional Controls */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '15px'
              }}>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      border: '2px solid white',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'Outfit',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="day_time">Sort by Day & Time</option>
                    <option value="module">Sort by Module</option>
                    <option value="lecturer">Sort by Lecturer</option>
                  </select>
                </div>

                <button
                  onClick={clearFilters}
                  style={{
                    padding: '8px 16px',
                    background: 'white',
                    color: '#F04915',
                    border: '2px solid white',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Clear All Filters
                </button>
              </div>
            </div>

            {/* Results Section */}
            <div style={{
              background: '#FFFBF5',
              padding: '30px',
              borderBottomLeftRadius: 15,
              borderBottomRightRadius: 15,
              minHeight: '400px'
            }}>
              {loading ? (
                <div style={{
                  textAlign: 'center',
                  padding: '50px',
                  fontSize: '16px',
                  color: '#666',
                  fontFamily: 'Outfit'
                }}>
                  Loading timetables...
                </div>
              ) : authError ? (
                <div style={{
                  textAlign: 'center',
                  padding: '50px',
                  fontSize: '16px',
                  color: '#dc3545',
                  fontFamily: 'Outfit'
                }}>
                  {authError}
                </div>
              ) : filteredTimetables.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '50px',
                  fontSize: '16px',
                  color: '#666',
                  fontFamily: 'Outfit'
                }}>
                  {timetables.length === 0 ? 'No timetables found.' : 'No timetables match your search criteria.'}
                </div>
              ) : (
                <>
                  {/* Results Count */}
                  <div style={{
                    marginBottom: '20px',
                    fontSize: '14px',
                    color: '#666',
                    fontFamily: 'Outfit',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span>
                      Showing {filteredTimetables.length} of {timetables.length} timetable entries
                      {Object.entries(filters).filter(([key, value]) => value !== '' && value !== 'all').length > 0 && (
                        <span style={{color: '#F04915', marginLeft: 8}}>
                          (filtered)
                        </span>
                      )}
                    </span>
                    
                    {filteredTimetables.length > 0 && (
                      <button
                        onClick={downloadCSV}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#059669',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontSize: 12,
                          fontFamily: 'Outfit',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6
                        }}
                      >
                        📊 Export CSV ({filteredTimetables.length} entries)
                      </button>
                    )}
                  </div>

                  {/* Timetables Display */}
                  {viewMode === 'grid' ? renderListView() : renderTableView()}
                </>
              )}
            </div>

            {/* Back Button */}
            <div style={{display: 'flex', justifyContent: 'flex-start', marginTop: '20px'}}>
              <Link to="/timetable-management">
                <button 
                  type="button" 
                  style={{
                    width: 269, 
                    padding: '11px 20px', 
                    background: '#F04915', 
                    border: 'none', 
                    borderRadius: 8, 
                    color: 'white', 
                    fontSize: 16, 
                    fontFamily: 'Outfit', 
                    fontWeight: '500', 
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = '#D63A0F';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = '#F04915';
                  }}
                >
                  Back to Time Table Management
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedTimetable && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ width: '600px' }}>
            <h2 style={{
              fontSize: '24px',
              fontFamily: 'Outfit',
              fontWeight: '700',
              color: '#F04915',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              Timetable Details
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px',
              marginBottom: '30px'
            }}>
              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '5px', fontFamily: 'Outfit' }}>
                  Module
                </label>
                <div style={{ fontSize: '16px', color: '#6B7280', fontFamily: 'Outfit' }}>
                  {selectedTimetable.modules?.module_code} - {selectedTimetable.modules?.module_name}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '5px', fontFamily: 'Outfit' }}>
                  Day & Time
                </label>
                <div style={{ fontSize: '16px', color: '#6B7280', fontFamily: 'Outfit' }}>
                  {selectedTimetable.day_of_week}, {selectedTimetable.timeDisplay}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '5px', fontFamily: 'Outfit' }}>
                  Venue
                </label>
                <div style={{ fontSize: '16px', color: '#6B7280', fontFamily: 'Outfit' }}>
                  {selectedTimetable.venue}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '5px', fontFamily: 'Outfit' }}>
                  Class Type
                </label>
                <div style={{ fontSize: '16px', color: '#6B7280', fontFamily: 'Outfit' }}>
                  {selectedTimetable.class_type}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '5px', fontFamily: 'Outfit' }}>
                  Lecturer
                </label>
                <div style={{ fontSize: '16px', color: '#6B7280', fontFamily: 'Outfit' }}>
                  {selectedTimetable.lecturer?.name} ({selectedTimetable.lecturer?.staff_type})
                </div>
              </div>

              {selectedTimetable.assistant_lecturer && (
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '5px', fontFamily: 'Outfit' }}>
                    Assistant Lecturer
                  </label>
                  <div style={{ fontSize: '16px', color: '#6B7280', fontFamily: 'Outfit' }}>
                    {selectedTimetable.assistant_lecturer.name} ({selectedTimetable.assistant_lecturer.staff_type})
                  </div>
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '5px', fontFamily: 'Outfit' }}>
                  Year & Semester
                </label>
                <div style={{ fontSize: '16px', color: '#6B7280', fontFamily: 'Outfit' }}>
                  Year {selectedTimetable.year}, Semester {selectedTimetable.semester}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '5px', fontFamily: 'Outfit' }}>
                  Academic Year
                </label>
                <div style={{ fontSize: '16px', color: '#6B7280', fontFamily: 'Outfit' }}>
                  {selectedTimetable.academic_year}
                </div>
              </div>
            </div>

            {selectedTimetable.groupNames && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '5px', fontFamily: 'Outfit' }}>
                  Groups
                </label>
                <div style={{ fontSize: '16px', color: '#6B7280', fontFamily: 'Outfit' }}>
                  {selectedTimetable.groupNames}
                </div>
              </div>
            )}

            {selectedTimetable.degreeNames && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '5px', fontFamily: 'Outfit' }}>
                  Degrees
                </label>
                <div style={{ fontSize: '16px', color: '#6B7280', fontFamily: 'Outfit' }}>
                  {selectedTimetable.degreeNames}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '30px' }}>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  padding: '12px 30px',
                  background: '#F04915',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '16px',
                  fontFamily: 'Outfit',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ViewTimeTable;